import { useCallback, useEffect, useRef, useState } from 'react';

const useGpuOneFilter = (gpuData = []) => {
	// Local filters for immediate UI feedback
	const [localFilters, setLocalFilters] = useState({
		machineName: '',
		systemVendor: '',
		systemProduct: '',
		machineStatus: '',
		deployedOS: '',
		sort: '',
	});

	// API filters for actual API calls (updated on blur/submit)
	const [apiFilters, setApiFilters] = useState({
		machineName: '',
		systemVendor: '',
		systemProduct: '',
		machineStatus: '',
		deployedOS: '',
		sort: '',
	});

	// Since we're using server-side filtering, we don't need client-side filtering
	// Just return the data as-is since filtering is handled by the API
	const filteredGPUData = gpuData;

	const debounceTimeoutRef = useRef(null);

	// Handle local filter changes (for immediate UI feedback)
	const handleFilterChange = (fieldName, value) => {
		setLocalFilters(prev => ({
			...prev,
			[fieldName]: value,
		}));

		// Clear existing timeout
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		// Set new timeout for API call (2 seconds after user stops typing)
		debounceTimeoutRef.current = setTimeout(() => {
			setApiFilters(prev => ({
				...prev,
				[fieldName]: value,
			}));
		}, 500);
	};

	// Handle blur events (for immediate API calls when user leaves field)
	const handleFilterBlur = useCallback((fieldName, value) => {
		// Clear any pending debounced call
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		// Immediately update API filters
		setApiFilters(prev => ({
			...prev,
			[fieldName]: value,
		}));
	}, []);

	// Handle sort changes (immediate API call)
	const handleSortChange = useCallback(value => {
		setLocalFilters(prev => ({ ...prev, sort: value }));
		setApiFilters(prev => ({ ...prev, sort: value }));
	}, []);

	const handleClearFilters = () => {
		// Clear any pending debounced call
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		const emptyFilters = {
			machineName: '',
			systemVendor: '',
			systemProduct: '',
			machineStatus: '',
			deployedOS: '',
			sort: '',
		};
		setLocalFilters(emptyFilters);
		setApiFilters(emptyFilters);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, []);

	// Convert API filters to request body format
	const getApiFilters = () => {
		const requestBodyFilters = {};

		// Only include non-empty filters in the request body
		if (apiFilters.machineName)
			requestBodyFilters.machine_name = apiFilters.machineName;
		if (apiFilters.systemVendor)
			requestBodyFilters.system_vendor = apiFilters.systemVendor;
		if (apiFilters.systemProduct)
			requestBodyFilters.system_product = apiFilters.systemProduct;
		if (apiFilters.machineStatus)
			requestBodyFilters.machine_status = apiFilters.machineStatus;
		if (apiFilters.deployedOS)
			requestBodyFilters.deployed_os = apiFilters.deployedOS;

		return requestBodyFilters;
	};

	return {
		filters: localFilters, // For UI display
		apiFilters, // For API calls
		filteredGPUData,
		handleFilterChange,
		handleFilterBlur,
		handleSortChange,
		handleClearFilters,
		getApiFilters,
	};
};

export default useGpuOneFilter;
